import { useState } from "@wordpress/element";
import { Conversation, useConversations } from "@/hooks/use-conversations";
import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import Sidebar from "@/components/sidebar";
import { Messages } from "@/components/messages";
import { Center } from "@/components/center";
import { Spinner } from "@wordpress/components";
import { format } from "date-fns";
import { FaTrash } from "react-icons/fa";
import { api } from "@/config/api-fetch";
import { toast } from "react-toastify";
import { useModal } from "@/hooks/use-modal";

export function Conversations() {
    const { isLoading, data: conversations, refetch } = useConversations();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const { confirm } = useModal();

    const handleDeleteConversation = async (id: number) => {
        const confirmed = await confirm(__("Are you sure you want to delete this conversation?", "antimanual"));

        if (confirmed) {
            setDeleting(true);
            try {
                await api.delete(`/conversations/${id}`);
                await refetch();
                setSelectedId(null);
                toast.success(__("Conversation deleted successfully", "antimanual"));
            } catch (error) {
                toast.error(__("Failed to delete conversation", "antimanual"));
            }
            setDeleting(false);
        }
    };

    const selectedConversation = conversations?.find((c) => c.id === selectedId) || conversations?.[0];
    const toItem = (c: Conversation) => ({
        id: c.id.toString(),
        label: (
            <>
                <span>{c.title}</span>
                <div className={css("flex justify-between pt-2 text-gray")}>
                    <small>{format(new Date(c.created_at), "PPpp")}</small>
                    {selectedConversation?.id === c.id && (
                        <span
                            className={css("delete-conversation")}
                            title={__("Delete Conversation", "antimanual")}
                            onClick={() => handleDeleteConversation(c.id)}
                        >
                            {deleting ? <Spinner className={css("m-0")} /> : <FaTrash />}
                        </span>
                    )}
                </div>
            </>
        ),
    });

    return (
        <div className={css("conversations")}>
            {isLoading ? (
                <Center>
                    <Spinner />
                </Center>
            ) : !selectedConversation ? (
                <Center>
                    <div>{__("No conversations found", "antimanual")}</div>
                </Center>
            ) : (
                <Sidebar
                    selectedItem={toItem(selectedConversation)}
                    onSelectItem={(item) => setSelectedId(Number(item.id))}
                    items={conversations?.map(toItem) || []}
                    className="mt-0 border-none"
                >
                    <div className={css("chat-box")}>
                        <Messages messages={selectedConversation.messages.map((m) => ({ message: m.message, isReply: m.sender === "assistant" }))} />
                    </div>
                </Sidebar>
            )}
        </div>
    );
}
